// Global Instructions Rule Applied!
//
// Editable funding-round card (signed-off Mockup 3, rev 3) on the deal form's
// Execution tab. Edits the SHARED round entity (warning tag + linked deals
// note); the deal team picks the security type issued and — when it's a
// convertible type per cfg (deal_execution/convertible_security_types) — the
// instrument-terms panel appears (cap, discount, interest, maturity, MFN).
// Saves field-by-field through PATCH /rounds. Deals without a round get the
// link-existing picker plus "New round" (create-and-link via AddRoundModal —
// owner 2026-08-26: rounds belong to the company, deals link optionally);
// linked deals get Unlink, which removes only the link and keeps the round.
import React, { useEffect, useState } from 'react'
import { Button, Input, Modal, Select, Tag, Tooltip } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { DisconnectOutlined, PlusOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import InputNumber from '../../../core/components/InputNumber.jsx'
import dayjs from 'dayjs'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import AddRoundModal from '../../rounds/components/AddRoundModal.tsx'
import {
  DealExecutionCompanyRound,
  DealExecutionContext,
  DealExecutionRoundSummary
} from '../../../state_manager/data_models/deal_execution_board_model.ts'

const LOOKUP_CATEGORY_FUNDING_STAGE = 'Funding Stage'
const LOOKUP_CATEGORY_SECURITY_TYPE = 'Security Type'

interface RoundCardProps {
  dealId: string
  context: DealExecutionContext | null
  /** The deal's company — new rounds are created for it. */
  companyId?: string | null
  companyName?: string | null
  currencySymbol: string | null | undefined
  onPatchRound: (updates: { id: string } & Record<string, unknown>) => Promise<boolean>
  onLinkRound: (roundId: string | null) => Promise<boolean>
}

interface RoundAmountInputProps {
  value: number | null | undefined
  currencySymbol: string
  onCommit: (value: number | null) => void
}

// An amount on the round, saved when the field is left. The typed value is
// held here while editing: the shared InputNumber re-formats from its value
// prop whenever this card re-renders (a refetch, a lookup refresh), which
// put the stored amount back mid-edit and the blur then saw nothing to save.
function RoundAmountInput({ value, currencySymbol, onCommit }: RoundAmountInputProps): React.ReactElement {
  const [draft, setDraft] = useState<number | null>(value ?? null)
  useEffect(() => setDraft(value ?? null), [value])
  return (
    <InputNumber
      size='small'
      className='w-full'
      isCurrency
      currencySymbol={currencySymbol}
      value={draft ?? undefined}
      onChange={(next: number | string | null) => setDraft(next == null || next === '' ? null : Number(next))}
      onBlur={() => {
        if (draft !== (value ?? null)) onCommit(draft)
      }}
    />
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className='flex items-center gap-3 py-1'>
      <span className='w-40 flex-none text-xs text-gray-500'>{label}</span>
      <div className='flex-1 min-w-0'>{children}</div>
    </div>
  )
}

export default function RoundCard({
  dealId,
  context,
  companyId = null,
  companyName = null,
  currencySymbol,
  onPatchRound,
  onLinkRound
}: RoundCardProps): React.ReactElement {
  const { detailsByCategory } = useLookups()
  const [linkSelection, setLinkSelection] = useState<string | null>(null)
  const [linking, setLinking] = useState(false)
  const [newRoundOpen, setNewRoundOpen] = useState(false)
  const [modal, modalContextHolder] = Modal.useModal()

  const round: DealExecutionRoundSummary | null | undefined = context?.round
  const linkedDeals = context?.linked_deals || []
  const companyRounds: DealExecutionCompanyRound[] = context?.company_rounds || []

  const fundingStageOptions = (detailsByCategory[LOOKUP_CATEGORY_FUNDING_STAGE] || []).map((d) => ({
    value: d.id,
    label: d.label
  }))
  const securityTypeOptions = (detailsByCategory[LOOKUP_CATEGORY_SECURITY_TYPE] || []).map((d) => ({
    value: d.id,
    label: d.label
  }))

  const save = async (field: string, value: unknown): Promise<void> => {
    if (!round) return
    const ok = await onPatchRound({ id: round.rounds__id, [field]: value })
    if (!ok) showErrorToast('Failed to save round change')
  }

  if (!round) {
    return (
      <FormSection title='Funding Round' titleColor='blue' scrollable={false}>
        <div className='text-sm text-gray-500 mb-3'>
          No funding round linked. Link one of the company&apos;s rounds, or create a new one and
          link it in one step.
        </div>
        <div className='flex gap-2'>
          <Select
            placeholder='Company rounds…'
            className='min-w-[280px]'
            value={linkSelection ?? undefined}
            onChange={(value) => setLinkSelection(value)}
            options={companyRounds.map((r) => ({
              value: r.rounds__id,
              label: `${r.rounds__name || 'Unnamed round'}${
                r.rounds__round_close_date
                  ? ` · ${new Date(r.rounds__round_close_date).toLocaleDateString()}`
                  : ''
              }`
            }))}
          />
          <Button
            type='primary'
            disabled={!linkSelection}
            loading={linking}
            onClick={async () => {
              if (!linkSelection) return
              setLinking(true)
              try {
                const ok = await onLinkRound(linkSelection)
                if (!ok) showErrorToast('Failed to link round')
              } finally {
                setLinking(false)
              }
            }}
          >
            Link round
          </Button>
          <Button icon={<PlusOutlined />} disabled={!companyId} onClick={() => setNewRoundOpen(true)}>
            New round
          </Button>
        </div>
        <AddRoundModal
          open={newRoundOpen}
          companyId={companyId}
          companyName={companyName}
          onCancel={() => setNewRoundOpen(false)}
          onCreated={async (roundId) => {
            setNewRoundOpen(false)
            const ok = await onLinkRound(roundId)
            if (!ok) showErrorToast('Round created but linking it failed — link it from the picker')
          }}
        />
      </FormSection>
    )
  }

  return (
    <FormSection
      title='Funding Round'
      titleColor='blue'
      scrollable={false}
      headerActions={
        <>
          {modalContextHolder}
          <Tag color='gold' className='m-0'>
            Shared entity
          </Tag>
          {linkedDeals.length > 0 ? (
            <span className='text-xs text-gray-400'>
              Also linked to: {linkedDeals.map((d) => d.deals__name).join(', ')}
            </span>
          ) : null}
          <Tooltip title='Unlink this round from the deal'>
            <Button
              size='small'
              type='text'
              icon={<DisconnectOutlined />}
              onClick={() =>
                modal.confirm({
                  title: 'Unlink this round from the deal?',
                  content:
                    'Only the link is removed. The round itself is kept — it stays on the company and on any other deals linked to it.',
                  okText: 'Unlink',
                  onOk: async () => {
                    const ok = await onLinkRound(null)
                    if (!ok) showErrorToast('Failed to unlink the round')
                  }
                })
              }
            />
          </Tooltip>
        </>
      }
    >
      <Row label='Round name'>
        {/* keyed: a linked round or a name saved elsewhere replaces the text */}
        <Input
          key={`${round.rounds__id}:${round.rounds__name ?? ''}`}
          size='small'
          defaultValue={round.rounds__name || ''}
          onBlur={(e) => {
            const next = e.target.value.trim()
            if (next !== (round.rounds__name || '')) save('name', next || null)
          }}
        />
      </Row>
      <Row label='Funding stage'>
        <Select
          size='small'
          allowClear
          className='w-full'
          value={round.rounds__funding_stage ?? undefined}
          onChange={(value) => save('funding_stage', value ?? null)}
          options={fundingStageOptions}
        />
      </Row>
      <Row label='Round close date'>
        <DatePicker
          size='small'
          className='w-full'
          format='MM/DD/YYYY'
          value={round.rounds__round_close_date ? dayjs(round.rounds__round_close_date) : null}
          onChange={(value) => save('round_close_date', value ? value.format('YYYY-MM-DD') : null)}
        />
      </Row>
      <Row label='Security type issued'>
        <Select
          size='small'
          allowClear
          className='w-full'
          value={round.rounds__security_type ?? undefined}
          onChange={(value) => save('security_type', value ?? null)}
          options={securityTypeOptions}
        />
      </Row>
      <Row label='Raise amount'>
        <RoundAmountInput
          value={round.rounds__raise_amount}
          currencySymbol={currencySymbol || '$'}
          onCommit={(next) => save('raise_amount', next)}
        />
      </Row>
      <Row label='Pre-money valuation'>
        <RoundAmountInput
          value={round.rounds__pre_money_valuation}
          currencySymbol={currencySymbol || '$'}
          onCommit={(next) => save('pre_money_valuation', next)}
        />
      </Row>
      <Row label='Post-money valuation'>
        <RoundAmountInput
          value={round.rounds__post_money_valuation}
          currencySymbol={currencySymbol || '$'}
          onCommit={(next) => save('post_money_valuation', next)}
        />
      </Row>
      <Row label='Initial close amount'>
        <RoundAmountInput
          value={round.rounds__initial_close_amount}
          currencySymbol={currencySymbol || '$'}
          onCommit={(next) => save('initial_close_amount', next)}
        />
      </Row>

    </FormSection>
  )
}
